import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCamera,
  faCircleHalfStroke,
  faCrop,
  faDownload,
  faImage,
  faMoon,
  faRotateLeft,
  faRotateRight,
  faSun,
  faUndo,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import { AiImageCropModal } from "./AiImageCropModal";
import { applyCanvasFilterToBlob } from "../../../cores/helpers/apply-canvas-filter-to-blob";
import { rotateImageSrcToBlobUrl } from "../../../cores/helpers/rotate-image-to-blob-url";
import "./AiImageViewerEditorModal.scss";

type FilterPreset = {
  id: string;
  labelKey: string;
  css: string;
  icon: IconDefinition;
};

const FILTER_PRESETS: FilterPreset[] = [
  { id: "none", labelKey: "app.aiGeneration.viewerFilterOriginal", css: "", icon: faImage },
  {
    id: "grayscale",
    labelKey: "app.aiGeneration.viewerFilterBw",
    css: "grayscale(1)",
    icon: faCircleHalfStroke,
  },
  { id: "sepia", labelKey: "app.aiGeneration.viewerFilterSepia", css: "sepia(0.55)", icon: faCamera },
  {
    id: "warm",
    labelKey: "app.aiGeneration.viewerFilterWarm",
    css: "sepia(0.12) saturate(1.15) brightness(1.03)",
    icon: faSun,
  },
  {
    id: "cool",
    labelKey: "app.aiGeneration.viewerFilterCool",
    css: "saturate(1.1) hue-rotate(8deg) brightness(1.02)",
    icon: faMoon,
  },
  {
    id: "vivid",
    labelKey: "app.aiGeneration.viewerFilterVivid",
    css: "contrast(1.12) saturate(1.2)",
    icon: faBolt,
  },
];

function buildFilterCss(presetCss: string, brightnessPct: number): string {
  const b = brightnessPct / 100;
  const parts = [presetCss.trim(), `brightness(${b})`].filter(Boolean);
  return parts.join(" ");
}

type Props = {
  isOpen: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onApply: (blob: Blob) => void | Promise<void>;
  busy?: boolean;
};

export function AiImageViewerEditorModal({
  isOpen,
  imageSrc,
  onClose,
  onApply,
  busy = false,
}: Props) {
  const intl = useIntl();
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [filterPresetId, setFilterPresetId] = useState("none");
  const [brightness, setBrightness] = useState(100);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropBusy, setCropBusy] = useState(false);
  const [rotateBusyDir, setRotateBusyDir] = useState<null | "cw" | "ccw">(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const ownedUrlsRef = useRef<Set<string>>(new Set());
  const initialSrcRef = useRef<string | null>(null);

  const revokeOwned = useCallback((url: string | null) => {
    if (url && ownedUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      ownedUrlsRef.current.delete(url);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      ownedUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* empty */
        }
      });
      ownedUrlsRef.current.clear();
      setDisplaySrc(null);
      setCropOpen(false);
      setFilterPresetId("none");
      setBrightness(100);
      setApplyError(null);
      initialSrcRef.current = null;
      return;
    }
    if (!imageSrc) return;
    initialSrcRef.current = imageSrc;
    setDisplaySrc(imageSrc);
    setFilterPresetId("none");
    setBrightness(100);
    setApplyError(null);
    setCropOpen(false);
  }, [isOpen, imageSrc]);

  const presetCss =
    FILTER_PRESETS.find((p) => p.id === filterPresetId)?.css ?? "";
  const previewFilter = buildFilterCss(presetCss, brightness);

  const handleCropApply = async (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    ownedUrlsRef.current.add(url);
    setDisplaySrc((prev) => {
      revokeOwned(prev);
      return url;
    });
    setCropOpen(false);
  };

  const handleRotate = async (direction: "cw" | "ccw") => {
    if (!displaySrc || rotateBusyDir) return;
    setRotateBusyDir(direction);
    setApplyError(null);
    try {
      const url = await rotateImageSrcToBlobUrl(displaySrc, direction);
      ownedUrlsRef.current.add(url);
      setDisplaySrc((prev) => {
        revokeOwned(prev);
        return url;
      });
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerRotateError" }));
    } finally {
      setRotateBusyDir(null);
    }
  };

  const handleResetEdits = () => {
    if (!initialSrcRef.current) return;
    setFilterPresetId("none");
    setBrightness(100);
    setDisplaySrc((prev) => {
      revokeOwned(prev);
      return initialSrcRef.current;
    });
  };

  const handleDownload = async () => {
    if (!displaySrc) return;
    setExportBusy(true);
    setApplyError(null);
    try {
      const blob = await applyCanvasFilterToBlob(displaySrc, previewFilter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wembii-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerExportError" }));
    } finally {
      setExportBusy(false);
    }
  };

  const handleApplyToResult = async () => {
    if (!displaySrc) return;
    setExportBusy(true);
    setApplyError(null);
    try {
      const blob = await applyCanvasFilterToBlob(displaySrc, previewFilter);
      await onApply(blob);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerExportError" }));
    } finally {
      setExportBusy(false);
    }
  };

  const handleClose = () => {
    if (busy || exportBusy || cropBusy || rotateBusyDir) return;
    setCropOpen(false);
    onClose();
  };

  const locked = busy || exportBusy || !!rotateBusyDir;

  return (
    <>
      <Modal
        isOpen={isOpen && !!displaySrc && !cropOpen}
        toggle={handleClose}
        className="ai-image-viewer-modal"
        contentClassName="ai-image-viewer-modal__dialog-content"
        modalClassName="ai-image-viewer-modal__backdrop-layer"
        backdropClassName="ai-image-viewer-modal__backdrop"
        centered
        backdrop="static"
        fade={false}
        zIndex={2050}
      >
        <div className="ai-image-viewer-modal__chrome">
          <div className="ai-image-viewer-modal__header">
            <div className="ai-image-viewer-modal__header-brand">
              <span className="ai-image-viewer-modal__badge" aria-hidden>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                AI
              </span>
              <div>
                <h2 className="ai-image-viewer-modal__title">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerModalTitle" })}
                </h2>
                <p className="ai-image-viewer-modal__subtitle">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerModalSubtitle" })}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="ai-image-viewer-modal__close"
              onClick={handleClose}
              disabled={locked}
              aria-label={intl.formatMessage({ id: "app.aiGeneration.viewerCloseBtn" })}
            >
              ×
            </button>
          </div>

          <ModalBody className="ai-image-viewer-modal__body">
            {displaySrc && (
              <>
                <div className="ai-image-viewer-modal__preview-shell">
                  <div className="ai-image-viewer-modal__preview-grid" aria-hidden />
                  <div className="ai-image-viewer-modal__preview-glow" aria-hidden />
                  <div className="ai-image-viewer-modal__preview">
                    <img
                      src={displaySrc}
                      alt=""
                      style={{ filter: previewFilter || "none" }}
                    />
                  </div>
                </div>

                <section className="ai-image-viewer-modal__section" aria-labelledby="ai-viewer-filters-label">
                  <div className="ai-image-viewer-modal__section-head">
                    <span id="ai-viewer-filters-label" className="ai-image-viewer-modal__section-title">
                      {intl.formatMessage({ id: "app.aiGeneration.viewerFiltersLabel" })}
                    </span>
                    <span className="ai-image-viewer-modal__section-hint">
                      {intl.formatMessage({ id: "app.aiGeneration.viewerFiltersHint" })}
                    </span>
                  </div>
                  <div className="ai-image-viewer-modal__filters-scroll">
                    {FILTER_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={
                          "ai-image-viewer-modal__filter-pill" +
                          (filterPresetId === p.id
                            ? " ai-image-viewer-modal__filter-pill--active"
                            : "")
                        }
                        onClick={() => setFilterPresetId(p.id)}
                        disabled={locked}
                      >
                        <FontAwesomeIcon icon={p.icon} className="ai-image-viewer-modal__filter-pill-icon" />
                        <span>{intl.formatMessage({ id: p.labelKey })}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="ai-image-viewer-modal__tuning" aria-labelledby="ai-viewer-brightness-label">
                  <div className="ai-image-viewer-modal__tuning-inner">
                    <div className="ai-image-viewer-modal__brightness-head">
                      <FontAwesomeIcon icon={faSun} className="ai-image-viewer-modal__brightness-icon" />
                      <span id="ai-viewer-brightness-label">
                        {intl.formatMessage({ id: "app.aiGeneration.viewerBrightnessLabel" })}
                      </span>
                      <span className="ai-image-viewer-modal__brightness-value">{brightness}%</span>
                    </div>
                    <input
                      type="range"
                      className="ai-image-viewer-modal__range"
                      min={60}
                      max={140}
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      disabled={locked}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerBrightnessLabel",
                      })}
                    />
                  </div>

                  <div className="ai-image-viewer-modal__quick-tools">
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => handleRotate("ccw")}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateLeftTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateLeftTitle",
                      })}
                    >
                      {rotateBusyDir === "ccw" ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faRotateLeft} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerRotateLeftBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => handleRotate("cw")}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateRightTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateRightTitle",
                      })}
                    >
                      {rotateBusyDir === "cw" ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faRotateRight} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerRotateRightBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => setCropOpen(true)}
                      disabled={locked}
                    >
                      <FontAwesomeIcon icon={faCrop} />
                      <span>{intl.formatMessage({ id: "app.aiGeneration.cropBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={handleResetEdits}
                      disabled={locked}
                    >
                      <FontAwesomeIcon icon={faUndo} />
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerResetBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={handleDownload}
                      disabled={locked || exportBusy}
                    >
                      {exportBusy ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faDownload} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerDownloadBtn" })}</span>
                    </button>
                  </div>
                </section>

                <p className="ai-image-viewer-modal__foot-note">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerHint" })}
                </p>

                {applyError && (
                  <p className="ai-image-viewer-modal__error">{applyError}</p>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter className="ai-image-viewer-modal__footer">
            <button
              type="button"
              className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--ghost"
              onClick={handleClose}
              disabled={locked}
            >
              {intl.formatMessage({ id: "app.aiGeneration.viewerCloseBtn" })}
            </button>
            <button
              type="button"
              className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--primary"
              onClick={handleApplyToResult}
              disabled={locked || !displaySrc}
            >
              {exportBusy || busy ? (
                <>
                  <Spinner size="sm" className="ai-image-viewer-modal__btn-spinner" />
                  {intl.formatMessage({ id: "app.aiGeneration.viewerApplying" })}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                  {intl.formatMessage({ id: "app.aiGeneration.viewerApplyBtn" })}
                </>
              )}
            </button>
          </ModalFooter>
        </div>
      </Modal>

      <AiImageCropModal
        isOpen={cropOpen && !!displaySrc}
        imageSrc={displaySrc}
        onClose={() => !cropBusy && setCropOpen(false)}
        onApply={async (blob) => {
          setCropBusy(true);
          try {
            await handleCropApply(blob);
          } finally {
            setCropBusy(false);
          }
        }}
        busy={cropBusy}
      />
    </>
  );
}
